import '.n{width:100%;}.ssr-plugin'
import '.bf{overflow:hidden;}.ssr-plugin'
import '.d6{border:1px solid #ddd;}.ssr-plugin'
import '.d7{z-index:2;}.ssr-plugin'
import { useEffect, useRef } from 'react'
import { styled, Style } from 'inlines'
import L from 'leaflet'
import '@maplibre/maplibre-gl-leaflet'
import ReactDOMServer from 'react-dom/server'
import { MARKER_CONFIG, type MarkerType } from './markerConfig'

export type { MarkerType } from './markerConfig'

const OPENFREEMAP_STYLE = 'https://tiles.openfreemap.org/styles/positron'

const OPENFREEMAP_ATTRIBUTION =
  '<a href="https://openfreemap.org">OpenFreeMap</a> © <a href="https://www.openmaptiles.org/">OpenMapTiles</a> Data from <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'

export type Coordinates = {
  lat: number
  long: number
}

const MARKER_ICON_SIZE: [number, number] = [24, 24]
const CENTER: Coordinates = { lat: 52.1005, long: 5.6465 }

export type Marker = {
  marker: MarkerType
} & Coordinates

export type LeafletMapProps = {
  markers: Marker[]
  viewpoint?: Coordinates
  zoom?: number
  height?: string
  style?: Style
}

export const LeafletMap = ({
  markers,
  viewpoint,
  zoom = 13,
  height = '20rem',
  style,
}: LeafletMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<any>(null)

  useEffect(() => {
    const mapViewpoint = viewpoint ?? markers[0] ?? CENTER

    if (!mapRef.current) {
      return
    }

    if (!validateCoordinates(mapViewpoint)) {
      return
    }
    try {
      // Fix for default markers in React
      // @ts-expect-error - Leaflet icon fix for React
      delete L.Icon.Default.prototype._getIconUrl

      // Initialize map (maxBounds/minZoom recommended for MapLibre GL Leaflet compatibility)
      // Leaflet expects [southWest, northEast] with finite lng in [-180, 180]
      const map = L.map(mapRef.current, {
        maxBounds: [
          [-90, -180],
          [90, 180],
        ],
        maxBoundsViscosity: 1,
        minZoom: 1,
      }).setView(expand(mapViewpoint), zoom)
      mapInstanceRef.current = map

      // OpenFreeMap vector tiles via MapLibre GL Leaflet plugin
      // MapLibre GL requires WebGL; fall back to raster tiles in environments
      // where WebGL is unavailable (e.g. BrowserStack VMs, headless browsers).
      const canvas = document.createElement('canvas')
      const isWebGLAvailable = !!(
        window.WebGLRenderingContext &&
        (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
      )

      if (isWebGLAvailable) {
        L.maplibreGL({
          style: OPENFREEMAP_STYLE,
          attributionControl: {
            customAttribution: OPENFREEMAP_ATTRIBUTION,
          },
        }).addTo(map)
      } else {
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: OPENFREEMAP_ATTRIBUTION,
          maxZoom: 19,
        }).addTo(map)
      }

      // Add markers
      markers.forEach((marker) => {
        if (validateCoordinates(marker)) {
          const { component, anchor } = MARKER_CONFIG[marker.marker]
          const [w, h] = MARKER_ICON_SIZE
          const iconAnchor: [number, number] =
            anchor === 'bottom' ? [w / 2, h] : [w / 2, h / 2]

          const customIcon = L.divIcon({
            html: ReactDOMServer.renderToString(component),
            className: 'leaflet-custom-marker',
            iconSize: MARKER_ICON_SIZE,
            iconAnchor,
          })

          L.marker(expand(marker), { icon: customIcon }).addTo(map)
        }
      })

      // Recompute size after container is laid out (fixes tiles when map is in lazy/ClientOnly)
      requestAnimationFrame(() => {
        if (typeof map.invalidateSize === 'function') {
          map.invalidateSize()
        }
      })
    } catch (error) {
      console.error('Error initializing map:', error)
    }

    // Cleanup
    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove()
        } catch {
          // Ignore cleanup errors
        }
        mapInstanceRef.current = null
      }
    }
  }, [markers, viewpoint, zoom])

  return (
    <styled.div className="n bf d6 d7"
      ref={mapRef}
      style={{
        height,
        borderRadius: style?.borderRadius || '20px',
        ...style,
      }}
    />
  )
}

const expand = (coordinates: Coordinates): [number, number] => {
  return [coordinates.lat, coordinates.long]
}

const validateCoordinates = (coordinates: Coordinates): boolean => {
  const { lat, long } = coordinates
  if (
    typeof lat !== 'number' ||
    typeof long !== 'number' ||
    isNaN(lat) ||
    isNaN(long)
  ) {
    console.error('Invalid coordinates:', { lat, long })
    return false
  }
  return true
}
